import classNames from 'classnames';
import { uniqBy } from 'lodash';
import { ReactNode, useCallback, useState } from 'react';
import ReactSelect, { components, OptionProps, Props as SelectProps, ValueContainerProps } from 'react-select';

import { ButtonLink } from '../../../Button/ButtonLink';
import { getWillowSelectStyles, WILLOW_SELECT_THEME } from './styles';

import s from './WillowMultiSelect.module.scss';

type OptionType = { label: ReactNode | JSX.Element; value: string | number };
function Option<TOptions extends OptionType, TIsMulti extends boolean>({
  isSelected,
  onSelectOnly,
  ...props
}: OptionProps<TOptions, TIsMulti> & { onSelectOnly?: (option: TOptions) => void }) {
  return (
    <div>
      <components.Option {...{ isSelected, ...props }}>
        <div className={classNames('d-flex gap-2', s.option)}>
          <input type="checkbox" className="form-check-input" checked={isSelected} onChange={() => null} />
          <label
            className="u-color-bark4"
            style={{ textOverflow: 'ellipsis', maxWidth: '85%', overflow: 'hidden', whiteSpace: 'nowrap' }}
          >
            {props.data.label}
          </label>
          {onSelectOnly && (
            <ButtonLink
              className={s.only}
              // react-select selects the option on events bubbling from the row, so keep the
              // button's interactions from reaching it
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onSelectOnly(props.data);
              }}
            >
              Only
            </ButtonLink>
          )}
        </div>
      </components.Option>
    </div>
  );
}

function ValueContainer<TOptions extends OptionType, TIsMulti extends boolean>({
  children,
  formatSelectedLabel,
  ...props
}: ValueContainerProps<TOptions, TIsMulti> & { formatSelectedLabel?: (option: TOptions) => ReactNode }) {
  if (!props.hasValue) return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const otherChildren = children ? children[1] : undefined;
  const selectedOptions = props.hasValue ? props.getValue() : [];
  const visibleOptions = selectedOptions.slice(0, 1);
  const remainingCount = selectedOptions.length - visibleOptions.length;

  return (
    <components.ValueContainer {...props}>
      <span
        style={{
          textOverflow: 'ellipsis',
          maxWidth: 'calc(100% - 10px)', // overflow space for ellipses + remove all icon
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        {visibleOptions.map((op) => (formatSelectedLabel ? formatSelectedLabel(op) : op.label))}
        {remainingCount > 0 && <> +{remainingCount}</>}
      </span>
      {otherChildren}
    </components.ValueContainer>
  );
}

export function WillowMultiSelect<TOptions extends OptionType>({
  onChange,
  onSearchQueryChange,
  formatSelectedLabel,
  onlyAndSelectAllEnabled,
  ...props
}: SelectProps<TOptions, true> & {
  onSearchQueryChange?: (query: string) => any;
  formatSelectedLabel?: (option: TOptions) => ReactNode;
  onlyAndSelectAllEnabled?: boolean;
  isInvalid?: boolean;
}) {
  const [inputValue, setInputValue] = useState('');

  // If the input value is being used to search, send the value up
  const handleSearchQueryChange = useCallback(
    (value: string) => {
      if (onSearchQueryChange) onSearchQueryChange(value);
    },
    [onSearchQueryChange],
  );

  const CustomValueContainer = useCallback(
    (containerProps: any) => <ValueContainer {...containerProps} formatSelectedLabel={formatSelectedLabel} />,
    [formatSelectedLabel],
  );

  const selectOnly = useCallback(
    (option: TOptions) => {
      // Only replaces the entire selection, so drop any typed search text — otherwise the
      // menu-close merge below would re-add options matching the stale input
      setInputValue('');
      onChange?.([option], { action: 'select-option', option });
    },
    [onChange],
  );

  const CustomOption = useCallback(
    (optionProps: any) => <Option {...optionProps} onSelectOnly={onlyAndSelectAllEnabled ? selectOnly : undefined} />,
    [onlyAndSelectAllEnabled, selectOnly],
  );

  const selectAll = useCallback(() => {
    const allOptions = Array.isArray(props.options) ? (props.options as TOptions[]) : [];
    onChange?.(allOptions, { action: 'select-option', option: undefined });
  }, [onChange, props.options]);

  const CustomMenuList = useCallback(
    (menuListProps: any) => (
      <components.MenuList {...menuListProps}>
        {onlyAndSelectAllEnabled && (
          // Match react-select's default option padding so the row lines up with the options below
          <div style={{ padding: '8px 12px' }}>
            <ButtonLink onClick={selectAll}>Select All</ButtonLink>
          </div>
        )}
        {menuListProps.children}
      </components.MenuList>
    ),
    [onlyAndSelectAllEnabled, selectAll],
  );

  return (
    <div className="multiselect-wrapper">
      <ReactSelect
        isMulti
        components={{
          Option: CustomOption,
          ValueContainer: CustomValueContainer,
          MenuList: CustomMenuList,
        }}
        hideSelectedOptions={false}
        closeMenuOnSelect={false}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onChange={onChange}
        styles={getWillowSelectStyles(props)}
        theme={WILLOW_SELECT_THEME}
        onInputChange={(inputString, meta) => {
          if (meta.action === 'input-change') {
            setInputValue(inputString);
            handleSearchQueryChange(inputString);
          }
          if (meta.action === 'set-value') {
            handleSearchQueryChange('');
          }
          // Now check with options were found and select them, making sure
          // to merge with existing values
          if (meta.action === 'menu-close') {
            const valuesToFind = inputValue.trim().split(/\s+|,/);
            const foundOptions = Array.isArray(props.options)
              ? props.options?.filter((option) => valuesToFind.includes(option.label))
              : [];
            const newValues = Array.isArray(props.value)
              ? uniqBy([...props.value, ...foundOptions], 'value')
              : foundOptions;
            onChange?.(newValues, { action: 'select-option', option: undefined });
            handleSearchQueryChange('');
          }
        }}
        {...props}
      />
    </div>
  );
}
